import { FC } from 'react';

import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

import { formatDate } from '../../utils/delivery.utils';
import { formatWon } from '../../utils/settlement.utils';
import { RebateRowCalc, RebateTotals } from '../../utils/rebate-table.utils';

// ─────────────────────────────────────────────
// RebateSettlementTable
//
// 상조 리베이트 정산 공용 테이블 (정산 예정 / 배송완료 공유).
// 계산은 rebate-table.utils 의 computeRebateRow/computeRebateTotals 가 담당하고
// 이 컴포넌트는 렌더링만 한다.
// ─────────────────────────────────────────────

/** `RebateSettlementTable` 컴포넌트의 props */
export interface RebateSettlementTableProps {
  /** 계산된 행 목록 */
  rows: RebateRowCalc[];
  /** 합계 */
  totals: RebateTotals;
  /** 행이 없을 때 표시할 메시지 */
  emptyMessage: string;
}

export const RebateSettlementTable: FC<RebateSettlementTableProps> = ({
  rows,
  totals,
  emptyMessage,
}) => {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto rounded-lg bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-xs text-gray-500">
          <tr>
            <th className="px-3 py-2 text-center text-sm">순번</th>
            <th className="px-3 py-2 text-left">
              <p className="text-sm">상품</p>
              <p className="mt-0.5 font-bold">구매자</p>
            </th>
            <th className="px-3 py-2 text-left">
              <p className="text-sm font-bold">배송완료일</p>
              <p className="mt-0.5">배송요청일</p>
            </th>
            <th className="px-3 py-2 text-left">
              <p className="text-sm">고인명</p>
              <p className="mt-0.5 font-bold">발인일</p>
            </th>
            <th className="px-3 py-2 text-right text-sm">판매가</th>
            <th className="px-3 py-2 text-right text-sm">-상품원가</th>
            <th className="px-3 py-2 text-right text-sm">
              -수수료 <span className="text-xs font-normal text-gray-400">(5.3%)</span>
            </th>
            <th className="px-3 py-2 text-right text-sm">=수익금</th>
            <th className="px-3 py-2 text-right text-sm">x수량</th>
            <th className="px-3 py-2 text-right text-sm">=수익금 합계</th>
            <th className="px-3 py-2 text-left">
              <p className="text-sm">지도사</p>
              <p className="mt-0.5 font-bold">연락처</p>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, idx) => (
            <tr key={idx} className="hover:bg-gray-50">
              <td className="whitespace-nowrap px-3 py-3 text-center text-xs tabular-nums text-gray-500">
                {idx + 1}
              </td>
              <td className="px-3 py-3">
                <p className="font-medium">{row.itemName}</p>
                <p className="mt-0.5 text-[11px] font-bold text-gray-700">
                  {row.receiverName}
                </p>
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                <p className="font-bold text-gray-700">
                  {formatDate(row.deliveredAt)}
                </p>
                <p className="mt-0.5">{formatDate(row.submittedAt)}</p>
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                <p>{row.deceasedName || '-'}</p>
                <p className="mt-0.5 font-bold text-gray-700">
                  {row.coffinOutDate ? formatDate(row.coffinOutDate) : '-'}
                </p>
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                {row.priceRetail != null ? row.priceRetail.toLocaleString() : '-'}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                {row.priceWhole != null ? row.priceWhole.toLocaleString() : '-'}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                {row.fee != null ? row.fee.toLocaleString() : '-'}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                {row.unitAmount != null ? row.unitAmount.toLocaleString() : '-'}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                {row.quantity}개
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-right">
                <span className="font-semibold tabular-nums text-gray-800">
                  {row.lineAmount != null ? formatWon(row.lineAmount) : '-'}
                </span>
                {!row.hasSettlementInfo && (
                  <span className="block text-[10px] text-amber-600">정산정보 미설정</span>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                <p>{row.workerName || '-'}</p>
                {row.workerPhoneNumber && (
                  <p className="mt-0.5 text-gray-400">
                    {phoneNumberStringFormator(row.workerPhoneNumber)}
                  </p>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-gray-50 text-sm font-semibold">
          <tr>
            <td className="px-3 py-3 text-gray-500" colSpan={8}>
              총 수익금 ({totals.totalCount}건)
            </td>
            <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
              {totals.totalQuantity}개
            </td>
            <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-rose-700">
              {/* 총판매가 − 총상품원가 − 총수수료(내림)로 역산된 값 */}
              {formatWon(totals.totalAmount)}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
      <div className="mt-2 space-y-0.5 text-xs text-gray-400">
        <p>* 수익금 합계 = (판매가 − 상품원가 − 수수료) × 수량의 합계</p>
        <p>* 수수료는 원 미만 절사</p>
      </div>
    </div>
  );
};
